import { ApiError, filenameFromDisposition, send } from "@h2j-data-team/http";
import { toStandardError } from "@repo/shared";
import * as Sentry from "@sentry/react";
import i18n from "@/i18n";

export { ApiError };

const ORGANIZATION_STORAGE_KEY = "senseguard.activeOrganizationId";
const ORGANIZATION_ID_HEADER = "x-organization-id";
export const API_V1_PREFIX = "/api/v1";

type SafeParseResult<T> =
  | { success: true; data: T }
  | { success: false; error: { flatten?: () => unknown } };

type Schema<T> = {
  safeParse: (input: unknown) => SafeParseResult<T>;
};

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export const API_BASE_URL =
  window.__APP_CONFIG__?.apiBaseUrl || import.meta.env.VITE_API_BASE_URL || "http://localhost:4011";

function getMessageForErrorCode(code: string | null, fallback: string): string {
  if (!code) return fallback;
  const translated = i18n.t(`errors:${code}`, { defaultValue: "" });
  return translated || fallback;
}

/**
 * The message the UI shows for a failed request. Qlik upstream failures get a
 * specific text. 5xx never show the server message, which can carry internal
 * detail: only the localized code or the generic text (the request ID is the
 * support code). 4xx keep the server message, then the localized code.
 */
function getUserFacingErrorMessage(error: ApiError): string {
  const details = error.details as {
    provider?: string;
    upstreamStatus?: number;
    reason?: string;
  } | null;

  if (details?.provider === "qlik") {
    if (details.reason === "invalid_credentials" || details.upstreamStatus === 401) {
      return i18n.t("errors:qlikAuthFailed");
    }
    if (details.reason === "forbidden" || details.upstreamStatus === 403) {
      return i18n.t("errors:qlikAccessDenied");
    }
    if (details.reason === "connection_failed") {
      return i18n.t("errors:qlikConnectionFailed");
    }
  }

  if (error.status >= 500) {
    return getMessageForErrorCode(error.code, i18n.t("errors:unexpectedError"));
  }
  // Without the envelope (proxy page, empty body) the message is only the status text.
  if (error.code === null) return getMessageForErrorCode(null, error.message);
  return error.message || getMessageForErrorCode(error.code, error.message);
}

function getDataPayload(payload: unknown): unknown {
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data?: unknown }).data;
  }
  return undefined;
}

function getActiveOrganizationId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const value = window.localStorage.getItem(ORGANIZATION_STORAGE_KEY)?.trim();
  return value ? value : null;
}

export function setActiveOrganizationId(organizationId: string | null): void {
  if (typeof window === "undefined") {
    return;
  }

  if (organizationId) {
    window.localStorage.setItem(ORGANIZATION_STORAGE_KEY, organizationId);
    return;
  }

  window.localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
}

export function getStoredActiveOrganizationId(): string | null {
  return getActiveOrganizationId();
}

export function parseOrThrow<T>(payload: unknown, schema: Schema<T>, message: string): T {
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw toStandardError({
      code: "BAD_RESPONSE",
      message,
      details: parsed.error?.flatten?.() ?? undefined,
    });
  }

  return parsed.data;
}

/**
 * Transport on the shared H2J `send`: credentials included, the active
 * organization in `x-organization-id`, `X-Requested-With` on every request (the
 * API CSRF guard), a Sentry breadcrumb per call, 5xx reported to Sentry with the
 * request ID, and the error envelope read into an `ApiError` carrying the
 * message the UI shows plus `fields`, `details` and `requestId`.
 */
async function transport<T>(
  path: string,
  init: {
    method?: Method;
    body?: unknown;
    headers?: Record<string, string>;
    responseType?: "auto" | "blob";
    signal?: AbortSignal;
  },
) {
  const method = init.method ?? "GET";
  const headers = new Headers(init.headers);
  headers.set("X-Requested-With", "XMLHttpRequest");
  const organizationId = getActiveOrganizationId();
  if (organizationId) headers.set(ORGANIZATION_ID_HEADER, organizationId);

  try {
    const res = await send<T>(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: init.body as BodyInit | object | undefined,
      responseType: init.responseType,
      signal: init.signal,
    });
    Sentry.addBreadcrumb({
      category: "api",
      message: `${method} ${path}`,
      data: { status: res.status },
      level: "info",
    });
    return res;
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    Sentry.addBreadcrumb({
      category: "api",
      message: `${method} ${path}`,
      data: { status: error.status },
      level: "warning",
    });
    const shown = new ApiError({
      status: error.status,
      code: error.code ?? "HTTP_ERROR",
      message: getUserFacingErrorMessage(error),
      fields: error.fields,
      details: error.details,
      requestId: error.requestId,
      body: error.body,
    });
    if (error.status >= 500) {
      Sentry.captureException(shown, {
        tags: error.requestId ? { requestId: error.requestId } : undefined,
        extra: { path, method, status: error.status },
      });
    }
    throw shown;
  }
}

export async function apiRequest(params: {
  path: string;
  method?: Method;
  body?: unknown;
  headers?: Record<string, string>;
  expectedStatus?: number;
  signal?: AbortSignal;
}): Promise<{ status: number; payload: unknown }> {
  const res = await transport<unknown>(params.path, params);
  if (params.expectedStatus !== undefined && res.status === params.expectedStatus) {
    return { status: res.status, payload: null };
  }
  return { status: res.status, payload: res.data ?? null };
}

export async function apiRequestJson<T>(params: {
  path: string;
  method?: Method;
  body?: unknown;
  responseSchema: Schema<T>;
  badResponseMessage: string;
  signal?: AbortSignal;
}): Promise<T> {
  const { payload } = await apiRequest({
    path: params.path,
    method: params.method,
    body: params.body,
    signal: params.signal,
  });

  return parseOrThrow(payload, params.responseSchema, params.badResponseMessage);
}

export async function apiRequestData<T>(params: {
  path: string;
  method?: Method;
  body?: unknown;
  dataSchema: Schema<T>;
  badResponseMessage: string;
}): Promise<T> {
  const { payload } = await apiRequest({
    path: params.path,
    method: params.method,
    body: params.body,
  });

  return parseOrThrow(getDataPayload(payload), params.dataSchema, params.badResponseMessage);
}

/**
 * Fetch a file endpoint (with the org header + credentials that a plain anchor
 * download can't send) and trigger a browser download from the response blob.
 */
export async function apiDownload(path: string, fallbackFilename: string): Promise<void> {
  const res = await transport<Blob>(path, { responseType: "blob" });
  const filename =
    filenameFromDisposition(res.headers.get("content-disposition")) ?? fallbackFilename;

  const url = URL.createObjectURL(res.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function getErrorMessage(err: unknown, fallback?: string): string {
  if (err && typeof err === "object") {
    const msg = (err as Record<string, unknown>).message;
    if (typeof msg === "string" && msg.trim().length > 0) return msg;
  }
  return fallback ?? i18n.t("errors:unexpectedError");
}
